import { Dialog, DialogBackdrop, DialogPanel } from '@headlessui/react';
import { ChevronLeftIcon, ChevronRightIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import dict from 'locales/dict';
import { useEffect, useRef, useState } from 'react';
import { ImageVideo } from 'shared/interfaces';

interface Props {
  images: ImageVideo[];
  previewIndex: any;
  setPreviewIndex: (value: any) => void;
}

export function ImagePreviewDialog({ images, previewIndex, setPreviewIndex }: Props) {
  const { t } = useTranslation();
  const hasPrev = previewIndex !== null && previewIndex > 0;
  const hasNext = previewIndex !== null && previewIndex < images.length - 1;
  const previewSrc = previewIndex !== null ? images[previewIndex] : null;
  const [zoom, setZoom] = useState(1);
  const imgRef = useRef<HTMLImageElement>(null);

  useEffect(() => {
    if (previewIndex === null) return;

    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft' && hasPrev) {
        setPreviewIndex((prev: any) => prev! - 1);
      }
      if (e.key === 'ArrowRight' && hasNext) {
        setPreviewIndex((prev: any) => prev! + 1);
      }
      if (e.key === 'Escape') {
        setPreviewIndex(null);
      }
    };

    const handleWheelZoom = (e: WheelEvent) => {
      if (!imgRef.current) return;

      e.preventDefault();

      // Lấy bounding box của hình
      const rect = imgRef.current.getBoundingClientRect();

      // Tính vị trí chuột tương đối trong hình (0 → 100)
      const offsetX = ((e.clientX - rect.left) / rect.width) * 100;
      const offsetY = ((e.clientY - rect.top) / rect.height) * 100;

      // Gán transform-origin theo vị trí chuột
      imgRef.current.style.transformOrigin = `${offsetX}% ${offsetY}%`;

      const delta = e.deltaY > 0 ? -0.1 : 0.1;

      setZoom((prev) => {
        const next = prev + delta;
        return Math.min(3, Math.max(1, next)); // 1x → 3x
      });
    };

    window.addEventListener('keydown', handleKey);
    window.addEventListener('wheel', handleWheelZoom, { passive: false });

    return () => {
      window.removeEventListener('keydown', handleKey);
      window.removeEventListener('wheel', handleWheelZoom);
      setZoom(1); // reset zoom khi đóng dialog
    };
    // eslint-disable-next-line
  }, [previewIndex, hasPrev, hasNext]);

  // console.log('render ImagePreviewDialog', images, previewIndex);

  return (
    <Dialog open={previewIndex !== null} onClose={() => setPreviewIndex(null)} className='relative z-[999]'>
      <DialogBackdrop
        transition
        className='fixed inset-0 hidden bg-gray-500/75 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 md:block'
      />

      <div className='fixed inset-0 z-[999] w-screen overflow-y-auto flex justify-center'>
        <div className='flex min-h-full items-stretch justify-center text-center md:items-center md:px-2 lg:px-4'>
          <DialogPanel
            transition
            className='flex w-full max-h-screen overflow-hidden
                      transform text-left text-base transition
                      md:my-4 md:max-w-10xl md:px-4
                      data-[closed]:opacity-0 data-[closed]:translate-y-4
                      data-[closed]:md:translate-y-0 data-[closed]:md:scale-100'
          >
            <div
              className='relative flex w-full flex-col
                        rounded-2xl
                        p-4
                        bg-brand-primary/11
                        backdrop-blur-md
                        shadow-2xl
                        border border-white/20
                        max-h-full overflow-hidden'
            >
              {/* Close */}
              <button
                type='button'
                onClick={() => setPreviewIndex(null)}
                className='absolute right-2 top-2
                  rounded-full p-1.5
                  bg-white/70 backdrop-blur-sm
                  shadow-lg
                  hover:bg-white hover:shadow-xl
                  transition z-20'
              >
                <span className='sr-only'>{t(dict.close)}</span>
                <XMarkIcon aria-hidden='true' className='size-6 text-gray-700' />
              </button>

              {/* Prev */}
              {hasPrev && (
                <button
                  onClick={() => setPreviewIndex(previewIndex! - 1)}
                  className='absolute left-2 top-1/2 -translate-y-1/2 rounded-full bg-black/40 p-2 hover:bg-black/60 z-20'
                >
                  <ChevronLeftIcon className='size-6 text-white' />
                </button>
              )}

              {/* Next */}
              {hasNext && (
                <button
                  onClick={() => setPreviewIndex(previewIndex! + 1)}
                  className='absolute right-2 top-1/2 -translate-y-1/2 rounded-full bg-black/40 p-2 hover:bg-black/60 z-20'
                >
                  <ChevronRightIcon className='size-6 text-white' />
                </button>
              )}

              {/* Text caption hình */}
              {previewSrc?.caption && (
                <div
                  className='
                  absolute left-1/2 -translate-x-1/2
                  top-5 px-4 py-1.5
                  rounded-full
                  bg-black/40
                  backdrop-blur-sm
                  text-white
                  shadow-lg
                  whitespace-nowrap
                  z-20
                '
                >
                  {previewSrc.caption}
                </div>
              )}

              {/* Image */}
              <div className='flex justify-center w-full max-h-screen overflow-hidden transition-transform duration-150 z-10'>
                <img
                  ref={imgRef}
                  alt='preview'
                  src={previewSrc?.src}
                  className='max-h-[calc(100vh-60px)] w-auto object-contain mx-auto rounded-2xl object-contain transition-transform duration-75'
                  style={{
                    transform: `scale(${zoom})`,
                  }}
                />
              </div>
            </div>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
}
